import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useCardDetailsStore } from '@lib/store';
import { useAccountContext, useResponsive, useRouteHook } from '@shared/hooks';
import { FormatUtils, PaletteUtils, RoutePaths } from '@shared/utils';
import { type AccountEntity } from '@shared/api';
import { Header } from './header';
import { AccountInfo } from './accountInfo';
import { CardHolderSelect } from '../cardHolderSelect';
import { CardImage } from '../cardImage';
import { Button } from '@shared/widgets';

const THEME = process.env.THEME;

const getValues = (account: AccountEntity | null, isMobile: boolean) =>
  [account?.mainCard, ...(account?.supplementCards || [])].map((card) => ({
    id: card?.id,
    name: card?.cardHolder,
    cardNumber: FormatUtils.formatCardNumber(card?.number, { short: isMobile }),
    cardType: card?.cardType,
  }));

enum AvailableOperations {
  BLOCK = 'Block',
  CHANGE_LIMIT = 'ChangeLimit',
  PIN_REMINDER = 'PinReminder',
  SUPPLEMENTARY = 'Supplementary',
}

interface SidebarProps {
  className?: string;
}

export const Sidebar = (props: SidebarProps) => {
  const location = useLocation();
  const { navigate } = useRouteHook();

  const cardDetailsStore = useCardDetailsStore();

  const { account, card } = useAccountContext();
  const { isMobile } = useResponsive();
  const { t } = useTranslation();

  useEffect(() => {
    if (!card) return;
    cardDetailsStore.getCardDetails(card.id);
  }, [card]);

  const values = getValues(account, isMobile);
  const isActiveButton = (path: string) => {
    if (isMobile && path.includes('transactions')) return location.pathname.includes('transactions');
    return location.pathname === path;
  };
  const getActiveButtonStyles = (isActive: boolean) =>
    isActive ? (PaletteUtils.isBordierPalette ? '!bg-[#dc291c] text-white' : '!bg-[#e5c496] !text-[#27272b] hover:!text-[#e5c496]') : '';

  const operations = useMemo(
    () => ({
      [AvailableOperations.CHANGE_LIMIT]: THEME !== 'm2' ? { url: 'limit', caption: t('sidebar.menu.manageCardLimit') } : null,
      [AvailableOperations.PIN_REMINDER]: { url: 'pin', caption: t('sidebar.menu.pinReminder') },
      [AvailableOperations.BLOCK]: { url: 'lost', caption: t('sidebar.menu.lostStolenCards') },
      [AvailableOperations.SUPPLEMENTARY]: { url: 'request-supplementary-card', caption: t('sidebar.menu.requestSupplementaryCard') },
    }),
    [t],
  );
  if (!account || !card) return null;
  return (
    <div className={clsx(props.className)}>
      {!isMobile && <Header name={account?.product?.name} />}
      <CardImage
        dark={account.product.isDarkCardColor}
        className='max-[840px]:hidden'
        image={card.cardDesignImageUrl || account.product?.cardImageUrl}
        cardHolder={card.cardHolder}
        number={FormatUtils.formatCardNumber(card.number)}
        validThru={FormatUtils.formatValidThru(cardDetailsStore.data?.expiryDate)}
      />
      <CardHolderSelect
        className='w-auto my-5 -mx-5 z-1'
        values={values}
        value={values.find((v) => card && card.id === v.id)}
        onChange={(card) => {
          const basePath = RoutePaths.card.onNavigate(card?.id);
          const shouldAddStatements = window.location.pathname.includes('/statements');
          navigate(shouldAddStatements ? `${basePath}/statements` : basePath);
        }}
      />
      <AccountInfo />
      <div className='flex mt-5'>
        <Button
          className={clsx(
            'px-0 mr-[10px] tracking-normal my-[10px] max-sm:text-base max-sm:font-[600] max-sm:leading-[1em] max-sm:h-[50px] max-sm:pt-[calc((50px-1em)/2)] max-sm:pb-[calc((50px-1em)/2)]',
            PaletteUtils.isBordierPalette && '!border-[#dc291c] hover:!bg-[#dc291c]',
            getActiveButtonStyles(
              isMobile
                ? location.pathname.includes('transactions')
                : isActiveButton(RoutePaths.transactions.onNavigate(card.id)) || isActiveButton(RoutePaths.card.onNavigate(card.id)),
            ),
          )}
          fullWidth
          caption={t('sidebar.menu.transactions')}
          url={isMobile ? RoutePaths.transactions.onNavigate(card.id) : RoutePaths.card.onNavigate(card.id)}
        />
        <Button
          className={clsx(
            'tracking-normal px-0 my-[10px] max-sm:text-[16px] max-sm:font-[600] max-sm:leading-[1em] max-sm:h-[50px] max-sm:pt-[calc((50px-1em)/2)] max-sm:pb-[calc((50px-1em)/2)]',
            PaletteUtils.isBordierPalette && ' !border-[#dc291c]  hover:!bg-[#dc291c]',
            getActiveButtonStyles(isActiveButton(RoutePaths.statements.onNavigate(card.id))),
          )}
          fullWidth
          caption={t('sidebar.menu.statements')}
          url={RoutePaths.statements.onNavigate(card.id)}
        />
      </div>
      {Object.entries(operations)
        .filter(([_, value]) => !!value)
        .map(([operationKey, operationValue]) => {
          const operationPath = `/card/${card.id}/${operationValue?.url}`;
          const isActive = isActiveButton(operationPath);

          return (
            <Button
              key={operationKey}
              className={clsx(
                'tracking-normal my-2.5 px-0 max-sm:text-base max-sm:font-[600] max-sm:leading-[1em] max-sm:h-[50px] max-sm:pt-[calc((50px-1em)/2)] max-sm:pb-[calc((50px-1em)/2)]',
                PaletteUtils.isBordierPalette && ' !border-[#dc291c] hover:!bg-[#dc291c]',
                !account.validOperations?.includes(operationKey) && '!hidden',
                getActiveButtonStyles(isActive),
              )}
              fullWidth
              caption={operationValue?.caption}
              url={operationPath}
            />
          );
        })}
    </div>
  );
};
